import Modal from "react-modal";
import React, { useMemo, useState } from "react";
import Files from "react-files";
import { http } from "../../services/http";
import authenticatedUserContext from "../security/AuthenticatedUserContext";

/**
 * Preview de importação — console operacional (Matriz / planilhas).
 * Foco: o que vai aplicar vs o que vai pular; diffs longos legíveis.
 */
const TOKENS = {
  ink: "#1a2332",
  mute: "#5c6b7a",
  rail: "#d8e0e8",
  paper: "#ffffff",
  wash: "#f4f7fa",
  apply: "#1f7a4d",
  applyWash: "#e8f5ee",
  skip: "#9a3412",
  skipWash: "#fff4ed",
  insert: "#1d4ed8",
  insertWash: "#eff6ff",
  warn: "#a16207",
  warnWash: "#fffbeb",
};

const customStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    maxWidth: "95vw",
    maxHeight: "92vh",
    overflow: "auto",
    padding: "20px 22px 16px",
    border: `1px solid ${TOKENS.rail}`,
    borderRadius: "6px",
  },
};

const previewStyles = {
  content: {
    ...customStyles.content,
    width: "980px",
  },
};

Modal.setAppElement("#root");

const PAGE_SIZE = 15;

function campoValor(obj, campo) {
  if (!obj || obj[campo] == null || obj[campo] === "") return null;
  return String(obj[campo]);
}

function DiffBlock({ label, antes, depois, emphasize }) {
  const a = antes == null || antes === "" ? null : String(antes);
  const d = depois == null || depois === "" ? null : String(depois);
  if (a == null && d == null) return null;
  if (a === d) {
    return (
      <div style={{ marginBottom: emphasize ? 8 : 4 }}>
        <span style={styles.diffLabel}>{label}</span>
        <div style={styles.diffSame}>{d || "—"}</div>
      </div>
    );
  }
  return (
    <div style={{ marginBottom: emphasize ? 8 : 4 }}>
      <span style={styles.diffLabel}>{label}</span>
      {a ? <div style={styles.diffOld}>{a}</div> : null}
      <div style={styles.diffNew}>{d || "—"}</div>
    </div>
  );
}

function AcaoPill({ acao }) {
  const map = {
    erro: { label: "Não importa", bg: TOKENS.skipWash, color: TOKENS.skip },
    atualizar: { label: "Atualizar", bg: TOKENS.applyWash, color: TOKENS.apply },
    inserir: { label: "Inserir", bg: TOKENS.insertWash, color: TOKENS.insert },
    ignorada: { label: "Ignorada", bg: TOKENS.warnWash, color: TOKENS.warn },
  };
  const m = map[acao] || { label: acao, bg: TOKENS.wash, color: TOKENS.mute };
  return (
    <span
      style={{
        display: "inline-block",
        fontSize: "11px",
        fontWeight: 600,
        letterSpacing: "0.01em",
        padding: "2px 8px",
        borderRadius: "3px",
        background: m.bg,
        color: m.color,
        whiteSpace: "nowrap",
      }}
    >
      {m.label}
    </span>
  );
}

function FilterChip({ active, onClick, children, tone }) {
  const tones = {
    default: active
      ? { bg: TOKENS.ink, color: "#fff", border: TOKENS.ink }
      : { bg: TOKENS.paper, color: TOKENS.ink, border: TOKENS.rail },
    apply: active
      ? { bg: TOKENS.apply, color: "#fff", border: TOKENS.apply }
      : { bg: TOKENS.paper, color: TOKENS.apply, border: "#b7dcc8" },
    skip: active
      ? { bg: TOKENS.skip, color: "#fff", border: TOKENS.skip }
      : { bg: TOKENS.paper, color: TOKENS.skip, border: "#f0c9b0" },
  };
  const t = tones[tone] || tones.default;
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        border: `1px solid ${t.border}`,
        background: t.bg,
        color: t.color,
        borderRadius: "4px",
        padding: "6px 12px",
        fontSize: "13px",
        fontWeight: active ? 600 : 500,
        cursor: "pointer",
        lineHeight: 1.2,
      }}
    >
      {children}
    </button>
  );
}

function ImportRow({ row }) {
  const isErro = row.acao === "erro";
  const convenioMudou =
    campoValor(row.antes, "convenio") !== campoValor(row.depois, "convenio");
  const planoMudou =
    campoValor(row.antes, "plano") !== campoValor(row.depois, "plano");
  const outros = (row.camposAlterados || []).filter(
    (c) => c !== "convenio" && c !== "plano"
  );

  return (
    <div
      style={{
        ...styles.row,
        borderLeft: `3px solid ${
          isErro
            ? TOKENS.skip
            : row.acao === "inserir"
            ? TOKENS.insert
            : TOKENS.apply
        }`,
        background: isErro ? TOKENS.skipWash : TOKENS.paper,
      }}
    >
      <div style={styles.rowMeta}>
        <span style={styles.linhaNum}>Linha {row.linha}</span>
        <AcaoPill acao={row.acao} />
        {row.id != null ? (
          <span style={styles.idMute}>id {row.id}</span>
        ) : null}
      </div>

      {isErro ? (
        <div style={styles.erroMsg}>{row.erro || "Linha inválida"}</div>
      ) : null}

      {row.aviso && !isErro ? (
        <div style={{ ...styles.erroMsg, color: TOKENS.warn }}>{row.aviso}</div>
      ) : null}

      {(convenioMudou || campoValor(row.depois, "convenio")) && (
        <DiffBlock
          label="Convênio"
          antes={campoValor(row.antes, "convenio")}
          depois={campoValor(row.depois, "convenio")}
        />
      )}

      {(planoMudou ||
        campoValor(row.depois, "plano") ||
        (isErro && campoValor(row.depois, "plano"))) && (
        <DiffBlock
          label="Plano"
          antes={campoValor(row.antes, "plano")}
          depois={campoValor(row.depois, "plano")}
          emphasize
        />
      )}

      {!isErro && outros.length > 0 ? (
        <div style={styles.outrosWrap}>
          {outros.map((c) => (
            <DiffBlock
              key={c}
              label={c.toUpperCase()}
              antes={campoValor(row.antes, c)}
              depois={campoValor(row.depois, c)}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

const styles = {
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "16px",
    marginBottom: "14px",
  },
  title: {
    margin: 0,
    fontSize: "18px",
    fontWeight: 700,
    color: TOKENS.ink,
    letterSpacing: "-0.01em",
  },
  fileName: {
    margin: "4px 0 0",
    fontSize: "13px",
    color: TOKENS.mute,
    wordBreak: "break-all",
  },
  hero: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "10px",
    marginBottom: "12px",
  },
  heroApply: {
    background: TOKENS.applyWash,
    border: `1px solid #c5e6d3`,
    borderRadius: "5px",
    padding: "12px 14px",
  },
  heroSkip: {
    background: TOKENS.skipWash,
    border: `1px solid #f0c9b0`,
    borderRadius: "5px",
    padding: "12px 14px",
  },
  heroMuted: {
    background: TOKENS.wash,
    border: `1px solid ${TOKENS.rail}`,
    borderRadius: "5px",
    padding: "12px 14px",
  },
  heroNum: {
    fontSize: "28px",
    fontWeight: 700,
    lineHeight: 1.1,
    color: TOKENS.ink,
    fontVariantNumeric: "tabular-nums",
  },
  heroLabel: {
    fontSize: "12px",
    color: TOKENS.mute,
    marginTop: "2px",
  },
  heroHint: {
    fontSize: "12px",
    color: TOKENS.mute,
    marginTop: "6px",
    lineHeight: 1.35,
  },
  filters: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
    alignItems: "center",
    marginBottom: "10px",
  },
  list: {
    maxHeight: "46vh",
    overflow: "auto",
    border: `1px solid ${TOKENS.rail}`,
    borderRadius: "5px",
    background: TOKENS.wash,
    padding: "8px",
  },
  row: {
    borderRadius: "4px",
    padding: "10px 12px",
    marginBottom: "8px",
    border: `1px solid ${TOKENS.rail}`,
  },
  rowMeta: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: "8px",
    marginBottom: "8px",
  },
  linhaNum: {
    fontWeight: 700,
    fontSize: "13px",
    color: TOKENS.ink,
    fontVariantNumeric: "tabular-nums",
  },
  idMute: {
    fontSize: "12px",
    color: TOKENS.mute,
    fontVariantNumeric: "tabular-nums",
  },
  erroMsg: {
    fontSize: "13px",
    color: TOKENS.skip,
    lineHeight: 1.4,
    marginBottom: "8px",
    padding: "8px 10px",
    background: "rgba(255,255,255,0.7)",
    borderRadius: "3px",
  },
  diffLabel: {
    display: "block",
    fontSize: "11px",
    color: TOKENS.mute,
    marginBottom: "2px",
  },
  diffOld: {
    fontSize: "12px",
    color: TOKENS.mute,
    textDecoration: "line-through",
    lineHeight: 1.35,
    wordBreak: "break-word",
  },
  diffNew: {
    fontSize: "13px",
    color: TOKENS.ink,
    fontWeight: 600,
    lineHeight: 1.35,
    wordBreak: "break-word",
  },
  diffSame: {
    fontSize: "13px",
    color: TOKENS.ink,
    lineHeight: 1.35,
    wordBreak: "break-word",
  },
  outrosWrap: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
    gap: "4px 12px",
    marginTop: "4px",
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
    marginTop: "12px",
    paddingTop: "12px",
    borderTop: `1px solid ${TOKENS.rail}`,
    flexWrap: "wrap",
  },
  pager: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "12px",
    color: TOKENS.mute,
  },
  actions: {
    display: "flex",
    gap: "8px",
    flexWrap: "wrap",
    marginLeft: "auto",
  },
};

export default function Import(props) {
  const [importIsOpen, setImportIsOpen] = useState(false);
  const [fileName, setFileName] = useState("");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("file"); // file | preview
  const [resumo, setResumo] = useState(null);
  const [alteracoes, setAlteracoes] = useState([]);
  const [pageIndex, setPageIndex] = useState(0);
  /** aplicar | erro | ignorada | all */
  const [filtroAcao, setFiltroAcao] = useState("aplicar");

  const confirmPreview = props.confirmPreview === true;

  const qtdAplicar = (resumo?.atualizar || 0) + (resumo?.inserir || 0);
  const qtdErros = resumo?.erros || 0;
  const qtdIgnoradas = resumo?.ignoradas || 0;

  const alteracoesVisiveis = useMemo(() => {
    const prioridade = (acao) => {
      if (acao === "erro") return 0;
      if (acao === "ignorada") return 1;
      if (acao === "inserir") return 2;
      if (acao === "atualizar") return 3;
      return 4;
    };
    const ordenadas = [...alteracoes].sort((a, b) => {
      const pa = prioridade(a.acao);
      const pb = prioridade(b.acao);
      if (pa !== pb) return pa - pb;
      return (a.linha || 0) - (b.linha || 0);
    });
    if (filtroAcao === "erro") {
      return ordenadas.filter((r) => r.acao === "erro");
    }
    if (filtroAcao === "ignorada") {
      return ordenadas.filter((r) => r.acao === "ignorada");
    }
    if (filtroAcao === "aplicar") {
      return ordenadas.filter(
        (r) => r.acao === "atualizar" || r.acao === "inserir"
      );
    }
    return ordenadas;
  }, [alteracoes, filtroAcao]);

  const errosResumo = useMemo(
    () => alteracoes.filter((r) => r.acao === "erro"),
    [alteracoes]
  );

  const totalPages = Math.max(
    1,
    Math.ceil(alteracoesVisiveis.length / PAGE_SIZE)
  );
  const pageRows = useMemo(() => {
    const start = pageIndex * PAGE_SIZE;
    return alteracoesVisiveis.slice(start, start + PAGE_SIZE);
  }, [alteracoesVisiveis, pageIndex]);

  function setFiltro(next) {
    setFiltroAcao(next);
    setPageIndex(0);
  }

  function onFilesChange(files) {
    var selected = files[0];
    setFile(selected);
    setFileName(selected?.name || "");
    setStep("file");
    setResumo(null);
    setAlteracoes([]);
    setPageIndex(0);
    setFiltroAcao("aplicar");
  }

  function onFilesError(error) {
    console.log("error code " + error.code + ": " + error.message);
  }

  function resetModalState() {
    setFileName("");
    setFile(null);
    setStep("file");
    setResumo(null);
    setAlteracoes([]);
    setPageIndex(0);
    setFiltroAcao("aplicar");
    setLoading(false);
  }

  function closeModal() {
    setImportIsOpen(false);
    resetModalState();
  }

  function buildFormData() {
    const formData = new FormData();
    formData.append(
      "upload",
      new Blob([file], { type: file.type }),
      file.name || "file"
    );
    return formData;
  }

  function showImportError(error) {
    if (error?.messages) {
      error.messages.map((erro_msg) => {
        props.alert.error("Erro. " + erro_msg?.error?.message, {
          timeout: 20000,
        });
      });
    } else if (error?.response?.data?.message) {
      props.alert.error("Erro. " + error.response.data.message, {
        timeout: 20000,
      });
    } else if (typeof error?.message === "string") {
      props.alert.error("Erro. " + error.message, { timeout: 20000 });
    } else {
      props.alert.error("Erro!");
    }
  }

  function analisar() {
    if (!file) {
      alert("Informe um arquivo para importação!");
      return;
    }

    setLoading(true);
    http
      .post(props.api_resource + "/import/preview", buildFormData())
      .then(
        (response) => {
          const data = response?.data || {};
          const lista = Array.isArray(data.alteracoes) ? data.alteracoes : [];
          const aplicar =
            ((data.resumo && data.resumo.atualizar) || 0) +
            ((data.resumo && data.resumo.inserir) || 0);
          setResumo(data.resumo || null);
          setAlteracoes(lista);
          // Mostra o que será aplicado; erros ficam no bloco de cima
          setFiltroAcao(aplicar > 0 ? "aplicar" : "erro");
          setPageIndex(0);
          setStep("preview");
        },
        (error) => {
          showImportError(error);
        }
      )
      .finally(() => {
        setLoading(false);
      });
  }

  function importar(excluir) {
    if (!file) {
      alert("Informe um arquivo para importação!");
      return;
    }

    setLoading(true);
    const formData = buildFormData();

    var url = props.api_resource + "/import";
    const params = [];
    if (excluir === true) {
      params.push("excluir=true");
    }
    params.push("confirm=true");
    url += "?" + params.join("&");

    http
      .post(url, formData)
      .then(
        (response) => {
          const resumoResp = response?.data?.resumo;
          if (resumoResp?.errosIgnorados > 0) {
            props.alert.success(
              `Importação concluída. ${resumoResp.aplicados || 0} linha(s) aplicadas; ${resumoResp.errosIgnorados} com erro ignorada(s).`,
              { timeout: 15000 }
            );
          } else {
            props.alert.success("Sucesso!");
          }
          setImportIsOpen(false);
          resetModalState();
          if (props.onFinish) {
            props.onFinish(response);
          }
        },
        (error) => {
          showImportError(error);
        }
      )
      .finally(() => {
        setLoading(false);
      });
  }

  function confirmarImportacao() {
    if (qtdAplicar === 0) {
      props.alert.error("Não há alterações para aplicar.");
      return;
    }
    importar(false);
  }

  function openImportModal() {
    resetModalState();
    setImportIsOpen(true);
  }

  const filtroLabel =
    filtroAcao === "erro"
      ? "erros"
      : filtroAcao === "ignorada"
      ? "ignoradas"
      : filtroAcao === "aplicar"
      ? "a aplicar"
      : "alterações";

  return (
    <>
      {authenticatedUserContext.allow_edit(props.appname) === true ? (
        <button
          className="btn btn-sm btn-success"
          style={{ marginTop: "20px", marginLeft: "10px" }}
          type="button"
          onClick={openImportModal}
        >
          Importar
        </button>
      ) : (
        <></>
      )}

      <Modal
        isOpen={importIsOpen}
        style={step === "preview" ? previewStyles : customStyles}
        contentLabel="Importação"
        onRequestClose={loading ? undefined : closeModal}
      >
        {loading ? (
          <div style={{ textAlign: "center", margin: "28px 12px" }}>
            <i
              className="fa fa-spinner fa-spin"
              style={{ fontSize: "2rem", color: TOKENS.mute }}
            ></i>
            <p style={{ color: TOKENS.mute, marginTop: "12px" }}>
              {step === "preview"
                ? "Aplicando importação..."
                : "Analisando planilha..."}
            </p>
          </div>
        ) : step === "preview" && confirmPreview ? (
          <div>
            <div style={styles.header}>
              <div>
                <h3 style={styles.title}>Revisar importação</h3>
                <p style={styles.fileName}>{fileName}</p>
              </div>
              <div style={{ fontSize: "12px", color: TOKENS.mute, textAlign: "right" }}>
                {resumo?.totalLinhas != null
                  ? `${resumo.totalLinhas} linhas na planilha`
                  : null}
                {resumo?.semAlteracao ? (
                  <div>{resumo.semAlteracao} sem mudança</div>
                ) : null}
              </div>
            </div>

            {resumo ? (
              <div
                style={{
                  ...styles.hero,
                  gridTemplateColumns: qtdErros > 0 ? "1fr 1fr" : "1fr",
                }}
              >
                <div style={styles.heroApply}>
                  <div style={{ ...styles.heroNum, color: TOKENS.apply }}>
                    {qtdAplicar}
                  </div>
                  <div style={styles.heroLabel}>serão aplicadas</div>
                  <div style={styles.heroHint}>
                    {resumo.atualizar || 0} atualizar
                    {resumo.inserir ? ` · ${resumo.inserir} inserir` : ""}
                  </div>
                </div>
                {qtdErros > 0 ? (
                  <div style={styles.heroSkip}>
                    <div style={{ ...styles.heroNum, color: TOKENS.skip }}>
                      {qtdErros}
                    </div>
                    <div style={styles.heroLabel}>serão puladas (erro)</div>
                    <div style={styles.heroHint}>
                      Linha
                      {errosResumo.length === 1 ? "" : "s"}{" "}
                      {errosResumo
                        .slice(0, 12)
                        .map((r) => r.linha)
                        .join(", ")}
                      {errosResumo.length > 12
                        ? ` +${errosResumo.length - 12}`
                        : ""}
                      . Não bloqueiam a importação.
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}

            {resumo?.idsIgnorados > 0 ? (
              <div
                style={{
                  ...styles.heroMuted,
                  marginBottom: "12px",
                  fontSize: "13px",
                  color: TOKENS.warn,
                }}
              >
                {resumo.idsIgnorados} id(s) inexistente(s) neste ambiente — linhas
                ignoradas, sem insert por fallback.
              </div>
            ) : null}

            <div style={styles.filters}>
              <FilterChip
                tone="apply"
                active={filtroAcao === "aplicar"}
                onClick={() => setFiltro("aplicar")}
              >
                A aplicar ({qtdAplicar})
              </FilterChip>
              <FilterChip
                tone="skip"
                active={filtroAcao === "erro"}
                onClick={() => setFiltro("erro")}
              >
                Erros ({qtdErros})
              </FilterChip>
              {qtdIgnoradas > 0 ? (
                <FilterChip
                  active={filtroAcao === "ignorada"}
                  onClick={() => setFiltro("ignorada")}
                >
                  Ignoradas ({qtdIgnoradas})
                </FilterChip>
              ) : null}
              <FilterChip
                active={filtroAcao === "all"}
                onClick={() => setFiltro("all")}
              >
                Todas ({alteracoes.length})
              </FilterChip>
            </div>

            <div style={styles.list}>
              {pageRows.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    color: TOKENS.mute,
                    padding: "28px 12px",
                    fontSize: "13px",
                  }}
                >
                  Nada neste filtro.
                </div>
              ) : (
                pageRows.map((row) => (
                  <ImportRow
                    key={`${row.linha}-${row.acao}-${row.id || "new"}`}
                    row={row}
                  />
                ))
              )}
            </div>

            <div style={styles.footer}>
              <div style={styles.pager}>
                <button
                  className="btn btn-default btn-sm"
                  type="button"
                  disabled={pageIndex <= 0}
                  onClick={() => setPageIndex((p) => Math.max(0, p - 1))}
                >
                  Anterior
                </button>
                <span>
                  {pageIndex + 1}/{totalPages} · {alteracoesVisiveis.length}{" "}
                  {filtroLabel}
                </span>
                <button
                  className="btn btn-default btn-sm"
                  type="button"
                  disabled={pageIndex >= totalPages - 1}
                  onClick={() =>
                    setPageIndex((p) => Math.min(totalPages - 1, p + 1))
                  }
                >
                  Próxima
                </button>
              </div>

              <div style={styles.actions}>
                <button
                  className="btn btn-default btn-sm"
                  type="button"
                  onClick={() => setStep("file")}
                >
                  Trocar arquivo
                </button>
                <button
                  className="btn btn-default btn-sm"
                  type="button"
                  onClick={closeModal}
                >
                  Cancelar
                </button>
                <button
                  className="btn btn-success btn-sm"
                  type="button"
                  disabled={qtdAplicar === 0}
                  onClick={confirmarImportacao}
                  style={{ fontWeight: 600, minWidth: "160px" }}
                >
                  {qtdErros > 0
                    ? `Aplicar ${qtdAplicar} (pular ${qtdErros})`
                    : `Aplicar ${qtdAplicar} alteraç${qtdAplicar === 1 ? "ão" : "ões"}`}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <form>
            <div className="form-group row">
              <div className="col-sm-12">
                <Files
                  className="files-dropzone"
                  onChange={onFilesChange}
                  onError={onFilesError}
                  accepts={[".xls", ".xlsx", ".csv"]}
                  multiple={false}
                  maxFileSize={10000000}
                  minFileSize={0}
                  clickable
                >
                  <div className="input-group">
                    <div className="custom-file">
                      <input
                        readOnly={true}
                        type="text"
                        className="form-control"
                        value={fileName}
                        style={{ width: "300px" }}
                      />
                      <label
                        className="custom-file-label"
                        htmlFor="inputGroupFile01"
                      >
                        {fileName}
                      </label>
                    </div>
                  </div>
                </Files>
              </div>
            </div>
            <div className="hr-line-dashed"></div>
            <div className="form-group row" style={{ textAlign: "right" }}>
              <div className="col-sm-12 col-sm-offset-2">
                <button
                  className="btn btn-primary btn-sm"
                  type="button"
                  onClick={closeModal}
                  style={{ width: "70px" }}
                >
                  Fechar
                </button>
                {confirmPreview ? (
                  <button
                    className="btn btn-sm btn-success"
                    style={{ marginLeft: "10px" }}
                    type="button"
                    onClick={analisar}
                  >
                    Analisar
                  </button>
                ) : (
                  <button
                    className="btn btn-sm btn-success"
                    style={{ marginLeft: "10px", width: "70px" }}
                    type="button"
                    onClick={() => importar(false)}
                  >
                    Importar
                  </button>
                )}
                {props.excluir === true && !confirmPreview ? (
                  <button
                    className="btn btn-sm btn-danger"
                    style={{ marginLeft: "10px", width: "150px" }}
                    type="button"
                    onClick={() => importar(true)}
                  >
                    Excluir e Importar
                  </button>
                ) : (
                  <></>
                )}
              </div>
            </div>
          </form>
        )}
      </Modal>
    </>
  );
}
